import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { makeStyles, createStyles } from '@material-ui/core';
import {
  Grid, Button, Card, CardContent, TextField, Box, IconButton, TablePagination
} from '@mui/material';
import {
  Formik, Form, FormikProps
} from 'formik';
import { Autocomplete } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';
import _ from 'lodash';

import { useAuth } from '../../../../contexts/auth';
import WrapperSimple from '../../../../layout-components/ExampleWrapperSimple';
import Dictionary from '../../../../models/interfaces/dictionary';
import { ClientPrepaidBalance } from '../../../../models/interfaces/balance';
import NoData from '../../../../components/noData';
import Loading from '../../../../components/loading';
import subsidiaryService from '../../../../services/subsidiaryService';
import balanceService from '../../../../services/balanceService';
import ExportCSV from '../../../../components/exportExcel';
import { phoneNumber, toBrazilDateTime } from '../../../../components/mask';
import PrepagoBalancePrint, { PrepaidBalanceExportRow } from './prepagoBalancePrint';

interface Formulario {
  subsidiary: string | null;
}

interface OwnProps {
  subsidiaryId?: string;
}

const PDF_MAX_ROWS = 2000;

const useStyles = makeStyles(() => createStyles({
  selectRow: {
    cursor: 'default'
  }
}));

const toMoney = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const page = (own: OwnProps) => {
  const [rows, setRows] = React.useState<ClientPrepaidBalance[] | undefined>();
  const current = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [subsidiaries, setSubsidiaries] = React.useState<Dictionary[]>([]);
  const [valuesForm, setValuesForm] = React.useState<Formulario>();
  const [pageIndex, setPageIndex] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const classes = useStyles();

  let { subsidiaryId } = current;
  if (own.subsidiaryId) subsidiaryId = own.subsidiaryId;

  useEffect(() => {
    if (subsidiaryId) return;
    const managerSubsidiaries = (current.subsidiaries ?? [])
      .filter((item) => item.key)
      .map((item) => ({ key: item.key, value: item.value }));
    if (managerSubsidiaries.length) {
      setSubsidiaries(managerSubsidiaries);
      return;
    }
    subsidiaryService.getByPaginationAsync(1, 200).then((result) => {
      setSubsidiaries(result.data.map((x: any) => ({
        key: x.id,
        value: x.description
      })));
    });
  }, [subsidiaryId, current.subsidiaries]);

  const convertToExportData = (balances: ClientPrepaidBalance[]): PrepaidBalanceExportRow[] => balances.map((item) => ({
    Cliente: item.clientName,
    Telefone: phoneNumber(item.phone) || '-',
    'Última atualização': item.lastApprovedAt ? toBrazilDateTime(item.lastApprovedAt) : '-',
    Saldo: toMoney(item.currentBalance)
  }));

  const getRowsToExcel = (): any[] => {
    const data = (rows ?? []).map((item) => ({
      Cliente: item.clientName,
      Telefone: phoneNumber(item.phone) || '-',
      'Última atualização': item.lastApprovedAt ? toBrazilDateTime(item.lastApprovedAt) : '-',
      Saldo: item.currentBalance
    }));

    if (data.length > 0) {
      data.push({
        Cliente: 'TOTAL',
        Telefone: `${data.length} cliente${data.length !== 1 ? 's' : ''}`,
        'Última atualização': '',
        Saldo: _.sumBy(rows, 'currentBalance')
      } as any);
    }

    return data;
  };

  const exportCsv = () => {
    const data = getRowsToExcel();
    if (!data.length) {
      enqueueSnackbar('Nenhum registro para exportar', { variant: 'warning' });
      return;
    }
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(';'),
      ...data.map((row) => headers.map((header) => {
        const value = row[header] ?? '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(';'))
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `saldo_prepago_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
  };

  const generatePDF = async () => {
    try {
      const allRows = convertToExportData(rows ?? []);
      const truncated = allRows.length > PDF_MAX_ROWS;
      const exportData = truncated ? allRows.slice(0, PDF_MAX_ROWS) : allRows;
      if (truncated) {
        enqueueSnackbar('O PDF foi limitado aos primeiros 2.000 registros. Use Excel ou CSV para a lista completa.', { variant: 'info' });
      }
      const pdfDocument = (
        <PrepagoBalancePrint
          data={exportData}
          fileName={format(new Date(), 'yyyyMMdd_HHmmss')}
          filters={{
            subsidiary: valuesForm?.subsidiary
              ? subsidiaries.find((item) => item.key === valuesForm.subsidiary)?.value
                || current.subsidiaries?.find((item) => item.key === valuesForm.subsidiary)?.value
                || 'Selecionada'
              : current.subsidiaries?.find((item) => item.key === subsidiaryId)?.value || 'Franquia do gestor'
          }}
          totalRecords={allRows.length}
          totalBalance={toMoney(_.sumBy(rows, 'currentBalance'))}
          truncated={truncated}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();
      saveAs(blob, `saldo_prepago_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    } catch {
      enqueueSnackbar('Erro ao gerar PDF', { variant: 'error' });
    }
  };

  const totals = React.useMemo(() => {
    if (!rows?.length) {
      return { clients: 0, balance: 0, withBalance: 0 };
    }
    return {
      clients: rows.length,
      balance: _.sumBy(rows, 'currentBalance'),
      withBalance: rows.filter((item) => item.currentBalance > 0).length
    };
  }, [rows]);

  const pagedRows = React.useMemo(() => {
    if (!rows) return [];
    const start = pageIndex * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, pageIndex, rowsPerPage]);

  return (
    <>
      <WrapperSimple sectionHeading="Relatório de saldo pré-pago por cliente">
        <Formik
          initialValues={{
            subsidiary: subsidiaryId ?? null
          }}
          validationSchema={Yup.object().shape({
            subsidiary: subsidiaryId ? Yup.string().notRequired().nullable() : Yup.string().required('Informe a franquia').nullable()
          })}
          onSubmit={async (values: Formulario, actions) => {
            setIsLoading(true);
            setValuesForm(values);
            setPageIndex(0);

            let subsidiaryIdSelected = subsidiaryId;
            if (!subsidiaryIdSelected) {
              subsidiaryIdSelected = values.subsidiary;
            }

            if (!subsidiaryIdSelected) {
              enqueueSnackbar('Informe a franquia', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            try {
              const report = await balanceService.getCurrentBalanceBySubsidiaryAsync(subsidiaryIdSelected);
              setRows(report);
            } catch (e: any) {
              enqueueSnackbar(e.message ?? 'Erro ao buscar o saldo pré-pago', { variant: 'error' });
              setRows([]);
            } finally {
              actions.setSubmitting(false);
              setIsLoading(false);
            }
          }}
        >
          {(props: FormikProps<Formulario>) => {
            const {
              errors,
              touched,
              handleBlur,
              isSubmitting,
              setFieldValue
            } = props;

            return (
              <Form noValidate autoComplete="off">
                <Grid container spacing={3} alignItems="center">
                  {subsidiaryId === undefined && (
                    <Grid item xs={12} sm={12} md={8}>
                      <Autocomplete
                        options={subsidiaries}
                        getOptionLabel={(option: Dictionary) => option.value}
                        onChange={(_e, value) => setFieldValue('subsidiary', value?.key ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            variant="outlined"
                            label="Franquia"
                            autoFocus
                            onBlur={handleBlur}
                            error={Boolean(errors.subsidiary && touched.subsidiary)}
                            helperText={errors.subsidiary && touched.subsidiary && errors.subsidiary}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={subsidiaryId === undefined ? 4 : 12}>
                    <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} fullWidth>
                      <span className="btn-wrapper--icon">
                        <FontAwesomeIcon icon={['far', 'keyboard']} />
                      </span>
                      <span className="btn-wrapper--label">Buscar</span>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </WrapperSimple>

      {rows && rows.length > 0 && (
        <Card className="card-box mb-4">
          <div className="card-header">
            <div className="card-header--title">
              <h4>Exportar Relatório</h4>
              <span className="text-black-50">
                {totals.clients}
                {' '}
                cliente
                {totals.clients !== 1 ? 's' : ''}
                {' · '}
                {totals.withBalance}
                {' com saldo'}
                {' · '}
                {toMoney(totals.balance)}
              </span>
            </div>
            <Box className="card-header--actions">
              <IconButton
                size="small"
                color="primary"
                className="text-primary"
                title="Exportar para PDF"
                onClick={generatePDF}
              >
                <FontAwesomeIcon icon={['far', 'file-pdf']} className="font-size-lg" />
              </IconButton>
              {' | '}
              <ExportCSV
                csvData={getRowsToExcel()}
                fileName={`saldo_prepago_${format(new Date(), 'yyyyMMdd_HHmmss')}`}
              />
              {' | '}
              <IconButton
                size="small"
                color="primary"
                className="text-primary"
                title="Exportar para CSV"
                onClick={exportCsv}
              >
                <FontAwesomeIcon icon={['far', 'file-alt']} className="font-size-lg" />
              </IconButton>
            </Box>
          </div>
        </Card>
      )}

      {isLoading ? <Loading /> : (
        <>
          {rows && (
            rows.length > 0 ? (
              <Card className="card-box mb-4">
                <CardContent className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover text-nowrap mb-0">
                      <thead>
                        <tr>
                          <th className="bg-white text-left">Cliente</th>
                          <th className="bg-white">Telefone</th>
                          <th className="bg-white text-center">Última atualização</th>
                          <th className="bg-white text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedRows.map((item) => (
                          <tr key={item.clientId} className={classes.selectRow}>
                            <td>{item.clientName}</td>
                            <td>{phoneNumber(item.phone) || '-'}</td>
                            <td className="text-center">
                              {item.lastApprovedAt ? toBrazilDateTime(item.lastApprovedAt) : '-'}
                            </td>
                            <td className="text-right">{toMoney(item.currentBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="font-weight-bold">
                        <tr>
                          <td className="text-right">TOTAL</td>
                          <td>{`${totals.clients} cliente${totals.clients !== 1 ? 's' : ''}`}</td>
                          <td className="text-center">{`${totals.withBalance} com saldo`}</td>
                          <td className="text-right">{toMoney(totals.balance)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <TablePagination
                    component="div"
                    count={rows.length}
                    page={pageIndex}
                    onPageChange={(_event, newPage) => setPageIndex(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPageIndex(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Linhas"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  />
                </CardContent>
              </Card>
            ) : <NoData message="Nenhum saldo pré-pago encontrado para a franquia informada." />
          )}
        </>
      )}
    </>
  );
};

export default page;
